import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMaxDiffCounts,
  fetchMaxDiffEmpiricalBayes,
  fetchMaxDiffTurf,
  fetchMaxDiffProbeAnalysis,
  fetchMaxDiffProbeCompare,
  fetchMaxDiffProbeItemThemes,
} from '../asyncActions/maxdiffAnalysis';

// Every MaxDiff sub-feature (counts, choiceProbability, hb, turf, ...) lives
// as its own sub-key with a { loading, data, error } envelope. New features
// slot in without touching existing keys — reducers stay decoupled from
// each other and selectors stay trivial.
type LoadableState<T> = {
  loading: boolean;
  data: T | null;
  error: string | null;
};

export type CountsItem = {
  item_id: number;
  name: string;
  best_count: number;
  worst_count: number;
  times_shown: number;
  best_proportion: number;
  worst_proportion: number;
  simple_count: number;
  // Legacy pairwise-based adjusted score kept as a debug/audit field.
  // Frontend chart now prefers the *_proportion fields below.
  adjusted_score: number;
  // Tie-break-adjusted proportions — treat each tie-break as an
  // additional best-picking event counted in BOTH numerator and
  // denominator. Bounded 0..1 with the same semantic as
  // `best_proportion` / `worst_proportion`.
  //   adjusted_best_proportion  = (best  + tie_top_wins) / (shown + tie_top_involvement)
  //   adjusted_worst_proportion = (worst + tie_bot_wins) / (shown + tie_bot_involvement)
  adjusted_best_proportion: number;
  adjusted_worst_proportion: number;
  // Per-item tie-break event counts — powering tooltips like
  // "won 4 of 8 top-tie contests".
  tie_top_wins: number;
  tie_top_involvement: number;
  tie_bot_wins: number;
  tie_bot_involvement: number;
};

export type CountsData = {
  items: CountsItem[];
  total_respondents: number;
  // Total directional pairwise votes derived from tie-break resolutions.
  // Zero means the sample had no ties → the "Include tie-break data"
  // toggle is a no-op and the UI hides it.
  tiebreak_votes_total: number;
};

export type CountsViewMode = 'best' | 'worst' | 'bestWorst' | 'simple';

// ── Empirical Bayes ─────────────────────────────────────────────
export type EBTransform = {
  value: number;
  ci_low: number;
  ci_high: number;
  // Tie-adjusted counterparts — same shape, computed by adding each
  // tie-break event to the respondent's own numerator+denominator
  // before running the EB shrinkage & bootstrap. Consumers pick which
  // pair to display via the `ebUseTiebreak` toggle.
  value_tb: number;
  ci_low_tb: number;
  ci_high_tb: number;
};
export type EBItem = {
  item_id: number;
  name: string;
  raw: EBTransform;
  zero_centered: EBTransform;
  probability: EBTransform;
};
export type EBData = {
  items: EBItem[];
  total_respondents: number;
  shrinkage_k: number;
  bootstrap_iters: number;
  // Aggregate tie-break event volume — used to auto-hide the "Include
  // tie-break data" toggle when the sample has zero ties.
  tiebreak_votes_total: number;
};
export type EBViewMode = 'raw' | 'zeroCentered' | 'probability';

// ── Probe Analysis ──────────────────────────────────────────────
export type ProbeModality = 'text' | 'audio' | 'video';
export type ProbeVerbatim = {
  user_id: number;
  name: string;
  modality: ProbeModality;
  // Text probes carry the response in `text`; audio/video probes have
  // it in the same field (backend COALESCEs from the transcript
  // column). null when a media probe is still awaiting transcription.
  text: string | null;
  // S3 URL for audio/video playback. null for text probes.
  url: string | null;
  // True when the respondent's overall top/bottom pick was resolved
  // via a tie-break screen (their raw Best-Worst scores tied at the
  // extreme, so they had to pick one when forced). Enables the
  // "X respondents · Y via tie-break" annotation on item tiles.
  tie_resolved: boolean;
};
export type ProbeItem = {
  item_id: number;
  name: string;
  order: number;
  best_probes: ProbeVerbatim[];
  worst_probes: ProbeVerbatim[];
};
export type ProbeAnalysisData = {
  items: ProbeItem[];
  total_respondents: number;
};

// ── Probe Compare (M4b) ─────────────────────────────────────────
// Head-to-head comparison of 2-4 items. One LLM call over the union
// of their probe verbatims returns a structured verdict + per-item
// unique wins/losses + trophy quotes + shared themes. Cached in the
// reducer keyed by sorted item-ids so re-selecting the same set
// short-circuits the LLM roundtrip.
type ProbeCompareTrophyQuote = {
  user_id: number;
  text: string;
  slot: 'best' | 'worst';
};
export type ProbeComparePerItem = {
  item_id: number;
  item_name: string;
  unique_wins: string[];
  unique_losses: string[];
  trophy_quotes: ProbeCompareTrophyQuote[];
};
export type ProbeCompareData = {
  verdict: string;
  per_item: ProbeComparePerItem[];
  shared_themes: string[];
  total_verbatims_analyzed: number;
  item_ids: number[];
};

// ── Probe Item Themes (M4c) ─────────────────────────────────────
// One fetch per question returns themes for every (item, slot) with
// count, uniqueness flag, and backing user_ids for click-to-filter.
// Cached in the reducer keyed by `${batchId}:${questionId}:${filter}`.
export type ProbeItemTheme = {
  label: string;
  count: number;
  // Powers the click-to-filter in VerbatimColumn — clicking a theme
  // chip narrows the verbatim list to just these user_ids.
  verbatim_user_ids: number[];
};
type ProbeItemThemesItem = {
  item_id: number;
  item_name: string;
  best_themes: ProbeItemTheme[];
  worst_themes: ProbeItemTheme[];
};
export type ProbeItemThemesData = {
  items: ProbeItemThemesItem[];
  total_verbatims_analyzed: number;
};

// ── TURF ────────────────────────────────────────────────────────
export type TurfCombination = {
  rank: number;
  items: number[];
  item_names: string[];
  reach: number;          // percentage 0..100
  reached_count: number;
  frequency: number;      // 1..combo_size
  combo_size: number;
};
export type TurfData = {
  combinations: TurfCombination[];
  total_respondents: number;
  combo_size: number;
  items_pool_size: number;
  total_combos_evaluated: number;
  appeal_mode: 'best' | 'positive_net';
  top_n: number;
};

type MaxDiffAnalysisState = {
  activeTab: 'counts' | 'eb' | 'turf' | 'probe';    // extend with 'hb' | ... later
  counts: LoadableState<CountsData>;
  countsView: CountsViewMode;
  countsRender: 'chart' | 'table';
  // "Include tie-break data" toggle. When true, Simple mode's chart
  // values and the ranking sort in the table use `adjusted_score` instead
  // of `simple_count`. Default true so the industry-first feature is
  // visible on landing; analysts who want raw numbers flip it off.
  countsUseTiebreak: boolean;
  eb: LoadableState<EBData>;
  ebView: EBViewMode;
  ebRender: 'chart' | 'table';
  ebSortOrder: 'desc' | 'asc';
  ebShowCi: boolean;
  // "Include tie-break data" toggle for the EB chart. Default true so
  // the tie-adjusted values are the analyst-facing default; flip off
  // to see the classic EB estimates alongside.
  ebUseTiebreak: boolean;
  probeAnalysis: LoadableState<ProbeAnalysisData>;
  // Active item id in the Probe Analysis workspace; null = show landing
  // (item grid). Selecting an item drills into its best/worst probes.
  probeActiveItemId: number | null;
  // AI-generated per-item summaries, keyed by
  // Compare-mode toggle: when true, tile clicks toggle selection
  // instead of drilling into deep-dive. Kept out of Redux persistence
  // scope — it's a per-session UI mode, not a saved preference.
  probeCompareMode: boolean;
  // Selection order preserved so the compare view can render columns
  // in the order the analyst picked them.
  probeCompareSelection: number[];
  // View flag — flipped true when the analyst clicks the Compare
  // action from the selection tray. Reset on Back / Exit compare.
  probeCompareOpen: boolean;
  // AI comparisons cached by sorted item-ids joined with a filter hash.
  // Cache key format: `${sortedItemIds.join(',')}:${filterHash}`.
  probeComparisons: Record<string, LoadableState<ProbeCompareData>>;
  // Per-(item, slot) themes for the whole question — one blob per
  // (batchId:questionId:filterHash) cache key. Fired lazily on first
  // deep-dive entry so a client who never drills in doesn't spend
  // the LLM call.
  probeItemThemes: Record<string, LoadableState<ProbeItemThemesData>>;
  turf: LoadableState<TurfData>;
  // null = use every item in the question. Sub-key stays trivially
  // serialisable so persistence / rehydration is a no-op.
  turfItemsPool: number[] | null;
  turfComboSize: number;
  // View controls (chart-only; don't trigger a refetch).
  turfMetric: 'reach' | 'frequency';
  turfSortOrder: 'desc' | 'asc';
  turfRender: 'chart' | 'table';
};

const emptyLoadable = <T>(): LoadableState<T> => ({
  loading: false,
  data: null,
  error: null,
});

const initialState: MaxDiffAnalysisState = {
  activeTab: 'counts',
  counts: emptyLoadable<CountsData>(),
  countsView: 'best',
  countsRender: 'chart',
  countsUseTiebreak: true,
  eb: emptyLoadable<EBData>(),
  // Default to Probability-scaled — matches the SurveyMonkey reference
  // default and is the most immediately useful scale for analysts
  // ("which item is most likely to be picked?").
  ebView: 'probability',
  ebRender: 'chart',
  ebSortOrder: 'desc',
  ebShowCi: false,
  ebUseTiebreak: true,
  probeAnalysis: emptyLoadable<ProbeAnalysisData>(),
  probeActiveItemId: null,
  probeCompareMode: false,
  probeCompareSelection: [],
  probeCompareOpen: false,
  probeComparisons: {},
  probeItemThemes: {},
  turf: emptyLoadable<TurfData>(),
  turfItemsPool: null,      // null = all items; component reads pool from Counts data
  turfComboSize: 3,
  turfMetric: 'reach',
  turfSortOrder: 'desc',
  turfRender: 'chart',
};

const maxdiffAnalysisSlice = createSlice({
  name: 'maxdiffAnalysis',
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<MaxDiffAnalysisState['activeTab']>) {
      state.activeTab = action.payload;
    },
    setCountsView(state, action: PayloadAction<CountsViewMode>) {
      state.countsView = action.payload;
    },
    setCountsRender(state, action: PayloadAction<'chart' | 'table'>) {
      state.countsRender = action.payload;
    },
    setCountsUseTiebreak(state, action: PayloadAction<boolean>) {
      state.countsUseTiebreak = action.payload;
    },
    setEbView(state, action: PayloadAction<EBViewMode>) {
      state.ebView = action.payload;
    },
    setEbRender(state, action: PayloadAction<'chart' | 'table'>) {
      state.ebRender = action.payload;
    },
    setEbSortOrder(state, action: PayloadAction<'desc' | 'asc'>) {
      state.ebSortOrder = action.payload;
    },
    setEbShowCi(state, action: PayloadAction<boolean>) {
      state.ebShowCi = action.payload;
    },
    setEbUseTiebreak(state, action: PayloadAction<boolean>) {
      state.ebUseTiebreak = action.payload;
    },
    setTurfItemsPool(state, action: PayloadAction<number[] | null>) {
      // null / [] → reset to "use all items"; deduped so repeated clicks
      // on the same item in a multi-select don't inflate the array.
      const v = action.payload;
      state.turfItemsPool = v && v.length > 0 ? Array.from(new Set(v)) : null;
    },
    setTurfComboSize(state, action: PayloadAction<number>) {
      state.turfComboSize = Math.max(1, Math.min(10, action.payload));
    },
    setTurfMetric(state, action: PayloadAction<'reach' | 'frequency'>) {
      state.turfMetric = action.payload;
    },
    setTurfSortOrder(state, action: PayloadAction<'desc' | 'asc'>) {
      state.turfSortOrder = action.payload;
    },
    setTurfRender(state, action: PayloadAction<'chart' | 'table'>) {
      state.turfRender = action.payload;
    },
    setProbeActiveItemId(state, action: PayloadAction<number | null>) {
      state.probeActiveItemId = action.payload;
    },
    setProbeCompareMode(state, action: PayloadAction<boolean>) {
      state.probeCompareMode = action.payload;
      // Turning compare off from anywhere resets selection & closes the
      // compare view — keeps state consistent regardless of which
      // control the analyst hit.
      if (!action.payload) {
        state.probeCompareSelection = [];
        state.probeCompareOpen = false;
      }
    },
    toggleProbeCompareItem(state, action: PayloadAction<number>) {
      const id = action.payload;
      const idx = state.probeCompareSelection.indexOf(id);
      if (idx >= 0) {
        state.probeCompareSelection.splice(idx, 1);
      } else if (state.probeCompareSelection.length < 4) {
        // Cap at 4 — matches backend guard and side-by-side column
        // layout ceiling.
        state.probeCompareSelection.push(id);
      }
    },
    clearProbeCompareSelection(state) {
      state.probeCompareSelection = [];
    },
    setProbeCompareOpen(state, action: PayloadAction<boolean>) {
      state.probeCompareOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMaxDiffCounts.pending, (state) => {
        state.counts.loading = true;
        state.counts.error = null;
      })
      .addCase(fetchMaxDiffCounts.fulfilled, (state, action) => {
        state.counts.loading = false;
        state.counts.data = action.payload;
      })
      .addCase(fetchMaxDiffCounts.rejected, (state, action) => {
        state.counts.loading = false;
        state.counts.error =
          (action.error?.message as string) || 'Failed to load counts';
      })
      .addCase(fetchMaxDiffEmpiricalBayes.pending, (state) => {
        state.eb.loading = true;
        state.eb.error = null;
      })
      .addCase(fetchMaxDiffEmpiricalBayes.fulfilled, (state, action) => {
        state.eb.loading = false;
        state.eb.data = action.payload;
      })
      .addCase(fetchMaxDiffEmpiricalBayes.rejected, (state, action) => {
        state.eb.loading = false;
        state.eb.error =
          (action.error?.message as string) ||
          'Failed to load Empirical Bayes results';
      })
      .addCase(fetchMaxDiffProbeAnalysis.pending, (state) => {
        state.probeAnalysis.loading = true;
        state.probeAnalysis.error = null;
      })
      .addCase(fetchMaxDiffProbeAnalysis.fulfilled, (state, action) => {
        state.probeAnalysis.loading = false;
        state.probeAnalysis.data = action.payload;
      })
      .addCase(fetchMaxDiffProbeAnalysis.rejected, (state, action) => {
        state.probeAnalysis.loading = false;
        state.probeAnalysis.error =
          (action.error?.message as string) ||
          'Failed to load probe analysis';
      })
      .addCase(fetchMaxDiffProbeCompare.pending, (state, action) => {
        const key = action.meta.arg.cacheKey;
        state.probeComparisons[key] = {
          loading: true,
          data: state.probeComparisons[key]?.data ?? null,
          error: null,
        };
      })
      .addCase(fetchMaxDiffProbeCompare.fulfilled, (state, action) => {
        state.probeComparisons[action.payload.key] = {
          loading: false,
          data: action.payload.data,
          error: null,
        };
      })
      .addCase(fetchMaxDiffProbeCompare.rejected, (state, action) => {
        const key = action.meta.arg.cacheKey;
        state.probeComparisons[key] = {
          loading: false,
          data: state.probeComparisons[key]?.data ?? null,
          error:
            (action.error?.message as string) ||
            'Failed to generate comparison',
        };
      })
      .addCase(fetchMaxDiffProbeItemThemes.pending, (state, action) => {
        const key = action.meta.arg.cacheKey;
        state.probeItemThemes[key] = {
          loading: true,
          data: state.probeItemThemes[key]?.data ?? null,
          error: null,
        };
      })
      .addCase(fetchMaxDiffProbeItemThemes.fulfilled, (state, action) => {
        state.probeItemThemes[action.payload.key] = {
          loading: false,
          data: action.payload.data,
          error: null,
        };
      })
      .addCase(fetchMaxDiffProbeItemThemes.rejected, (state, action) => {
        const key = action.meta.arg.cacheKey;
        state.probeItemThemes[key] = {
          loading: false,
          data: state.probeItemThemes[key]?.data ?? null,
          error:
            (action.error?.message as string) ||
            'Failed to extract themes',
        };
      })
      .addCase(fetchMaxDiffTurf.pending, (state) => {
        state.turf.loading = true;
        state.turf.error = null;
      })
      .addCase(fetchMaxDiffTurf.fulfilled, (state, action) => {
        state.turf.loading = false;
        state.turf.data = action.payload;
      })
      .addCase(fetchMaxDiffTurf.rejected, (state, action) => {
        state.turf.loading = false;
        state.turf.error =
          (action.error?.message as string) ||
          'Failed to load TURF results';
      });
  },
});

export const MaxDiffAnalysisSlice = maxdiffAnalysisSlice;
export const {
  setActiveTab,
  setCountsView,
  setCountsRender,
  setCountsUseTiebreak,
  setEbView,
  setEbRender,
  setEbSortOrder,
  setEbShowCi,
  setEbUseTiebreak,
  setTurfItemsPool,
  setTurfComboSize,
  setTurfMetric,
  setTurfSortOrder,
  setTurfRender,
  setProbeActiveItemId,
  setProbeCompareMode,
  toggleProbeCompareItem,
  clearProbeCompareSelection,
  setProbeCompareOpen,
} = maxdiffAnalysisSlice.actions;

export default maxdiffAnalysisSlice.reducer;
